import { FiAlertTriangle, FiClock, FiX } from "react-icons/fi";
import Button from "@/components/ui/Button";

interface SessionTimeoutWarningProps {
  minutesRemaining: number;
  isRefreshing: boolean;
  onRefresh: () => void;
  onDismiss: () => void;
}

/**
 * Warning banner shown when user session is about to expire
 * Prevents users from losing work by allowing them to refresh their session
 */
export function SessionTimeoutWarning({
  minutesRemaining,
  isRefreshing,
  onRefresh,
  onDismiss,
}: SessionTimeoutWarningProps) {
  return (
    <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-2xl px-4">
      <div className="bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-400 dark:border-amber-600 rounded-lg shadow-lg p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            <FiAlertTriangle className="h-6 w-6 text-amber-600 dark:text-amber-400" />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <FiClock className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-100">
                Session Expiring Soon
              </h3>
            </div>

            <p className="text-sm text-amber-800 dark:text-amber-200 mb-3">
              Your session will expire in{" "}
              <span className="font-bold">
                {minutesRemaining} {minutesRemaining === 1 ? "minute" : "minutes"}
              </span>
              . Any unsaved work will be lost. Click "Stay Logged In" to continue working.
            </p>

            <div className="flex items-center gap-2">
              <Button onClick={onRefresh} disabled={isRefreshing} size="sm" variant="primary">
                {isRefreshing ? "Refreshing..." : "Stay Logged In"}
              </Button>

              <Button
                onClick={onDismiss}
                size="sm"
                variant="ghost"
                className="text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-800"
              >
                Dismiss
              </Button>
            </div>
          </div>

          <button
            onClick={onDismiss}
            className="flex-shrink-0 p-1 hover:bg-amber-200 dark:hover:bg-amber-800 rounded transition"
            aria-label="Close warning"
          >
            <FiX className="h-5 w-5 text-amber-600 dark:text-amber-400" />
          </button>
        </div>
      </div>
    </div>
  );
}
